import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader } from "../card/card";
import ConfirmationDlg from "../ConfirmationDlg";
import NormalInput from "../form/HorizontalNormalInput";
import NormalSelect from "../form/HorizontalNormalSelect";
import NormalTextArea from "../form/HorizontalNormalTextarea";
import MultipleFilesAttachments from "../MultipleFilesAttachments";
import { user, workOrderAdditionalCostCategory } from "../../api";
import { toast } from "react-toastify";
import { formatDateFull } from "../../utils/helper";
import { useAuth } from "../../contexts/AuthContext";

const AdditionalCost = ({ value = [], onChange, readonly = false }) => {
  const { config } = useAuth();
  const [costEntries, setCostEntries] = useState(value);
  const [showAddModal, setShowAddModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [costCategories, setCostCategories] = useState([]);
  const [newCostEntry, setNewCostEntry] = useState({
    user: "",
    cost: "",
    category: "",
    description: "",
  });
  const [newCostCategory, setNewCostCategory] = useState("");
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (value.length > 0) {
      let newEntries = [];
      value.forEach((entry) => {
        const newEntry = {
          user: entry.user,
          cost: parseFloat(entry.cost) || 0,
          category: entry.category,
          description: entry.description || "",
          attachments: entry.attachments || [],
          created_at: entry.created_at,
        };

        newEntries.push(newEntry);
      });

      console.log(newEntries);
      setCostEntries(newEntries);
    }
  }, [value]);

  useEffect(() => {
    if (showAddModal) {
      getUsers();
      getCostCategories();
    }
  }, [showAddModal]);

  const getUsers = async () => {
    try {
      const res = await user.list({});
      if (res.ok) {
        const body = await res.json();
        let data = body.data;
        if (data) {
          data = data.map((d) => ({
            username: d.username,
            name: d.firstname + " " + d.lastname,
            value: d.username,
          }));
          setUsers(data);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const getCostCategories = async () => {
    try {
      const res = await workOrderAdditionalCostCategory.list({});
      if (res.ok) {
        const body = await res.json();
        let data = body.data;
        if (data && data.length > 0) {
          data = data.map((d) => ({
            id: d.id,
            name: d.name,
            value: d.id,
          }));
          setCostCategories(data);
        }
      } else {
        console.error("Cost categories API error:", res.status, res.statusText);
      }
    } catch (error) {
      console.error("Error fetching cost categories:", error);
    }
  };

  const handleAddNewCostCategory = async () => {
    if (!newCostCategory) {
      toast.error("Please enter a cost category name");
      return;
    }

    try {
      const result = await workOrderAdditionalCostCategory.create({
        name: newCostCategory,
      });

      if (result.ok) {
        const body = await result.json();
        const data = body.data;
        setCostCategories((prev) => [
          { id: data.id, name: data.name, value: data.id },
          ...prev,
        ]);
        setNewCostCategory("");
        toast.success("Cost category added successfully");
      } else {
        const errorBody = await result.json();
        toast.error(errorBody.message || "Failed to add cost category");
      }
    } catch (error) {
      console.error("Error adding cost category:", error);
      toast.error("Failed to add cost category");
    }
  };

  const handleAddCost = () => {
    if (!newCostEntry.user) {
      toast.error("Please select a user");
      return;
    }
    if (!newCostEntry.cost || parseFloat(newCostEntry.cost) <= 0) {
      toast.error("Please enter a valid cost amount");
      return;
    }
    if (!newCostEntry.category) {
      toast.error("Please select a cost category");
      return;
    }

    const cost = parseFloat(newCostEntry.cost);
    if (cost < 0) {
      toast.error("Cost cannot be negative");
      return;
    }

    const entry = {
      id: Date.now(),
      user: users.find((u) => u.value === newCostEntry.user),
      cost: cost,
      category: costCategories.find((c) => c.value === newCostEntry.category),
      description: newCostEntry.description,
      attachments: attachments,
      created_at: new Date().toISOString(),
    };

    const updatedEntries = [...costEntries, entry];
    setCostEntries(updatedEntries);
    onChange(updatedEntries);
    setShowAddModal(false);
    setNewCostEntry({
      user: "",
      cost: "",
      category: "",
      description: "",
    });
    setNewCostCategory("");
    setAttachments([]);
  };

  const handleDeleteCost = (index) => {
    const updatedEntries = costEntries.filter((_, i) => i !== index);
    setCostEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const calculateTotalCost = () => {
    return costEntries.reduce((total, entry) => {
      return total + entry.cost;
    }, 0);
  };

  const totalCost = calculateTotalCost();

  return (
    <Card style={{ marginTop: "10px" }}>
      <CardHeader>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>Additional Costs</div>
          {!readonly && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-sm btn-outline-theme"
            >
              Add Cost
            </button>
          )}
        </div>
      </CardHeader>
      <CardBody>
        {costEntries.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Category</th>
                  <th>Cost</th>
                  <th>Description</th>
                  <th>Date</th>
                  {!readonly && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {costEntries.map((entry, index) => (
                  <tr key={entry.id || index}>
                    <td>
                      {entry.user?.name ||
                        entry.user?.firstname + " " + entry.user?.lastname}
                    </td>
                    <td>
                      <span className="badge bg-primary">
                        {entry.category?.name}
                      </span>
                    </td>
                    <td>
                      {config?.currency_symbol}
                      {entry.cost.toFixed(2)}
                    </td>
                    <td>
                      {entry.description ? (
                        <span title={entry.description}>
                          {entry.description.length > 50
                            ? entry.description.substring(0, 50) + "..."
                            : entry.description}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{formatDateFull(entry.created_at)}</td>
                    {!readonly && (
                      <td>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteCost(index)}
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="table-info">
                  <td colSpan="2">
                    <strong>Total</strong>
                  </td>
                  <td>
                    <strong>
                      {config?.currency_symbol}
                      {totalCost.toFixed(2)}
                    </strong>
                  </td>
                  <td colSpan={readonly ? 2 : 3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="text-center text-muted py-4">
            <i className="fa fa-dollar-sign fa-3x mb-3"></i>
            <p>No cost entries yet.</p>
          </div>
        )}

        {/* Add Cost Modal */}
        <ConfirmationDlg
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddCost}
          title="Add Cost"
          btnSubmitText="Add"
          btnCancelText="Cancel"
          size="md"
        >
          <div className="row g-3">
            <div className="col-12">
              <NormalSelect
                label="User"
                value={newCostEntry.user}
                onchange={(e) =>
                  setNewCostEntry({ ...newCostEntry, user: e.target.value })
                }
                list={users}
                valueKey="value"
                required
                emptyField={[]}
              />
            </div>

            <div className="col-12">
              <NormalInput
                label="Cost"
                type="number"
                value={newCostEntry.cost}
                onchange={(e) =>
                  setNewCostEntry({ ...newCostEntry, cost: e.target.value })
                }
                name="cost"
                min={0}
                step="0.01"
                emptyField={[]}
                helperText={`Enter cost amount in ${
                  config?.currency_symbol || "$"
                }`}
              />
            </div>

            <div className="col-12">
              <NormalSelect
                label="Cost Category"
                value={newCostEntry.category}
                onchange={(e) =>
                  setNewCostEntry({ ...newCostEntry, category: e.target.value })
                }
                list={costCategories}
                valueKey="value"
                required
                emptyField={[]}
                onSubmit={handleAddNewCostCategory}
                addMoreComponent={
                  <NormalInput
                    required={true}
                    value={newCostCategory}
                    label="Cost Category"
                    onchange={(e) => {
                      setNewCostCategory(e.target.value);
                    }}
                  />
                }
              />
            </div>

            <div className="col-12">
              <NormalTextArea
                label="Description (Optional)"
                value={newCostEntry.description}
                onchange={(e) =>
                  setNewCostEntry({
                    ...newCostEntry,
                    description: e.target.value,
                  })
                }
                name="description"
                emptyField={[]}
                placeholder="Add a description"
              />
            </div>

            {/* <div className="col-12">
              <label className="form-label">Attachments</label>
              <MultipleFilesAttachments
                attachments={attachments}
                setAttachments={setAttachments}
              />
            </div> */}
          </div>
        </ConfirmationDlg>
      </CardBody>
    </Card>
  );
};

export default AdditionalCost;
